import { INSTANCE as NativeDownloadManager } from '@nativeAPP/nativeDownload/NativeDownloadManager'
import { DeviceTransmissionService } from '@core/transmission/DeviceTransmissionService'
import { INSTANCE as cloudAdapter } from '@/netadapters/CloudAdapter'
import { TransmissionState } from '@core/databus/IMessage'
import { AbsDownloadTask } from '@/core/transmission/AbsDownloadTask'
import { ILLEGAL_FILE_SIZE } from './AbsTask'
import type { FileInfo } from './IReadOnlyFileTransmissionManager'
import type { SpecificTaskInfo } from './ITask'
import { DeviceTaskError, TaskError, TaskState, TransmissionTaskType } from './ITask'
import { TransmissionDataBusProxy } from './TransmissionDataBusProxy'
import { STATUS_CODE_NATIVE_SERVICE_BIND_FAILED } from '@/netadapters/EndpointAdapter'
import { isEmpty } from 'lodash-es'
import { isInIosAPP, transmissionTagTracker } from '../Utils'
import type { TransmissionNoticeData } from '../databus/DataSyncManager'

export class DownloadTaskWithCloud extends AbsDownloadTask {
  urlTag = ''
  constructor(deviceId: string, fileInfo: FileInfo, appId: string, mirrorTask?: SpecificTaskInfo) {
    super(deviceId, fileInfo, false, appId, mirrorTask)
  }

  async start(): Promise<void> {
    const device = this._getDevice()

    if (!device) {
      this._terminateDueToDeviceNotFound()

      return
    }

    try {
      const notice = await TransmissionDataBusProxy.getInstance().appendNotice({
        appId: this._appId,
        isUpload: false,
        targetDevice: this._deviceId,
        targetPath: this._parentPath,
        fileName: this._fileName,
        fileSize: ILLEGAL_FILE_SIZE,
        transmitted: ILLEGAL_FILE_SIZE,
        state: TransmissionState.ON_GOING,
        ...(device.isSupportZip ? { businessType: TransmissionTaskType.DOWNLOAD } : {}),
      })

      console.log(`[Transmission] ${new Date().toLocaleString()} `, 'notice:', notice)

      if (!notice.data) {
        this._failedReason = TaskError.CREATE_NOTICE_FAILED
        this._taskScheduler?.setTaskError(this)

        return
      }
      this._preTransmissionNotice = JSON.stringify(notice.data)
      this._noticeId = notice.data.id
      const DeviceTransmissionResponse = await DeviceTransmissionService.uploadFile(
        device,
        this._parentPath + this._fileName,
        this.noticeId
      )

      console.log(
        `[Transmission] ${new Date().toLocaleString()}  fileName:`,
        this._fileName,
        'DeviceTransmissionResponse:uploadFile',
        DeviceTransmissionResponse
      )

      if (DeviceTransmissionResponse.isSuccess) {
        this._isInDeviceSideExecutingStage = true
        // this._checkLatestStatus()
        this.setTimer()
      } else {
        if (DeviceTransmissionResponse.RemoteServiceResponse?.statusCode === STATUS_CODE_NATIVE_SERVICE_BIND_FAILED) {
          this._failedReason = TaskError.NATIVE_SERVICE_BIND_FAILED
        } else {
          this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED
        }

        this._taskScheduler?.setTaskError(this)
      }
    } catch (error) {
      console.log(`[Transmission] ${new Date().toLocaleString()}  fileName:`, this._fileName, 'error:uploadFile', error)
      this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED

      this._handleError(error)
    }
  }

  async pause(): Promise<void> {
    if (!this._isInDeviceSideExecutingStage) {
      // 需要处理网页从云端下载文件的暂停场景，目前没有暂停所以暂不实现
      return
    }

    const device = this._getDevice()

    if (!device) {
      this._terminateDueToDeviceNotFound()

      return
    }

    try {
      const DeviceTransmissionResponse = await DeviceTransmissionService.pause(
        device,
        this._noticeId,
        true,
        TransmissionTaskType.DOWNLOAD
      )

      console.log(
        `[Transmission] ${new Date().toLocaleString()}  fileName:`,
        this._fileName,
        'DeviceTransmissionResponse:pause',
        DeviceTransmissionResponse
      )

      if (DeviceTransmissionResponse.isSuccess) {
        this.setTimer()
      } else {
        if (DeviceTransmissionResponse.RemoteServiceResponse?.statusCode === STATUS_CODE_NATIVE_SERVICE_BIND_FAILED) {
          this._failedReason = TaskError.NATIVE_SERVICE_BIND_FAILED
        } else {
          this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED
        }

        this._taskScheduler?.setTaskError(this)
      }
    } catch (error) {
      console.log(`[Transmission] ${new Date().toLocaleString()}  fileName:`, this._fileName, 'error:pause', error)
      this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED

      this._handleError(error)
    }
  }

  async resume(): Promise<void> {
    if (!this._isInDeviceSideExecutingStage) {
      if (isEmpty(this._downloadUrl)) {
        this.start()
      } else {
        this._onDeviceSideExecuteCompleted(this._downloadUrl)
      }

      return
    }

    const device = this._getDevice()

    if (!device) {
      this._terminateDueToDeviceNotFound()

      return
    }

    try {
      const DeviceTransmissionResponse = await DeviceTransmissionService.resume(
        device,
        this._noticeId,
        true,
        TransmissionTaskType.DOWNLOAD
      )

      console.log(
        `[Transmission] ${new Date().toLocaleString()}  fileName:`,
        this._fileName,
        'DeviceTransmissionResponse:resume',
        DeviceTransmissionResponse
      )

      if (!DeviceTransmissionResponse.isSuccess) {
        if (DeviceTransmissionResponse.RemoteServiceResponse?.statusCode === STATUS_CODE_NATIVE_SERVICE_BIND_FAILED) {
          this._failedReason = TaskError.NATIVE_SERVICE_BIND_FAILED
        } else {
          this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED
        }

        this._taskScheduler?.setTaskError(this)
      } else {
        // this._checkLatestStatus()
      }
    } catch (error) {
      console.log(`[Transmission] ${new Date().toLocaleString()}  fileName:`, this._fileName, 'error:resume', error)
      this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED

      this._handleError(error)
    }
  }

  async onRemove(): Promise<boolean> {
    this.removeUrlTag()
    this._deleteZipFile()

    //TODO: 处理任务删除失败情况，由于缺少UI反馈删除失败情况，目前均设置为删除任务成功
    if (!this._isInDeviceSideExecutingStage) {
      this.markAsDeleted()
      this._requestCancelToken?.cancel()
      this._requestCancelToken = undefined

      if (isInIosAPP()) {
        NativeDownloadManager.deleteTask(this.id)
      }

      return true
    }

    const device = this._getDevice()

    if (!device) {
      this._terminateDueToDeviceNotFound()

      return true
    }

    try {
      const DeviceTransmissionResponse = await DeviceTransmissionService.cancel(
        device,
        this._noticeId,
        true,
        TransmissionTaskType.DOWNLOAD
      )

      console.log(
        `[Transmission] ${new Date().toLocaleString()}  fileName:`,
        this._fileName,
        'DeviceTransmissionResponse:cancel',
        DeviceTransmissionResponse
      )

      if (!DeviceTransmissionResponse.isSuccess) {
        if (DeviceTransmissionResponse.RemoteServiceResponse?.statusCode === STATUS_CODE_NATIVE_SERVICE_BIND_FAILED) {
          this._failedReason = TaskError.NATIVE_SERVICE_BIND_FAILED
        } else {
          this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED
        }

        this._taskScheduler?.setTaskError(this)

        return true
      }

      return true
    } catch (error) {
      console.log(`[Transmission] ${new Date().toLocaleString()}  fileName:`, this._fileName, 'error:cancel', error)
      this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED

      this._handleError(error)

      return true
    }
  }

  /**
   * 根据通知消息更新任务状态
   * @param transmissionNotice
   * @param isFreezed
   * @returns
   */
  updateInfo(transmissionNotice: TransmissionNoticeData, isFreezed?: boolean): void {
    const { state, fileSize, transmitted, cloudPath, reason } = transmissionNotice

    if ([TaskState.SUCCESS, TaskState.FAILED, TaskState.PAUSE].includes(this.state)) return

    switch (state) {
      case TransmissionState.DONE_SUCCEED:
        this._fileSize = fileSize
        this._transmitted = transmitted
        console.log(`[Transmission]:cloudPath: ${this._fileName} ${new Date().toLocaleString()} `, cloudPath)
        this._completionRatio = 50
        this._fileDownloadListener?.onProgressChanged?.(this._transmitted, this._fileSize, this._deviceId, true)

        if (cloudPath && cloudPath !== 'null') {
          this.urlTag = transmissionTagTracker.takeTag()
          this._downloadUrl = cloudAdapter.getDownloadUrl(cloudPath, this.urlTag)
          this._taskScheduler?.updateTask(this)
          this._onDeviceSideExecuteCompleted(this._downloadUrl)
        } else {
          this._failedReason = TaskError.DEVICE_NOTIFIED_FAILURE_MESSAGE
          this._taskScheduler?.setTaskError(this)
        }
        this._checkLatestStatus.cancel()
        this.clearTimer()

        break
      case TransmissionState.DONE_FAILED:
        console.log(
          `[Transmission] ${new Date().toLocaleString()}  fileName:`,
          this._fileName,
          'device notifies  task has failed:',
          state
        )

        this._failedReason = TaskError.DEVICE_NOTIFIED_FAILURE_MESSAGE
        this._taskScheduler?.setTaskError(this)
        this.clearTimer()
        this._checkLatestStatus.cancel()
        break
      case TransmissionState.PAUSE:
        this.clearTimer()
        this._checkLatestStatus.cancel()

        if (reason === DeviceTaskError.DEVICE_CANCEL) {
          this._failedReason = DeviceTaskError.DEVICE_CANCEL
          this._deleteZipFile()
          this._handleError(DeviceTaskError.DEVICE_CANCEL)
        }
        break
      case TransmissionState.ON_GOING:
        console.log(
          `[Transmission] wan download task ${new Date().toLocaleString()} ${this._fileName} 下载任务进度更新:`,
          transmitted,
          fileSize
        )
        if (this._state === TaskState.FAILED || this._state === TaskState.SUCCESS) return

        this._checkLatestStatus()
        if (fileSize === ILLEGAL_FILE_SIZE || transmitted === ILLEGAL_FILE_SIZE) return
        this._fileSize = fileSize
        this._transmitted = transmitted > this._transmitted ? transmitted : this._transmitted
        this._completionRatio = fileSize === 0 ? 0 : Math.floor((transmitted / fileSize) * 50)
        console.log(
          `[Transmission] wan download task ${new Date().toLocaleString()} ${this._fileName} 下载任务进度更新:`,
          this._completionRatio
        )
        this._taskScheduler?.updateTask(this, isFreezed)
        this._fileDownloadListener?.onProgressChanged?.(this._transmitted, this._fileSize, this._deviceId, true)

        break
      default:
        break
    }
  }

  removeUrlTag() {
    if (!isEmpty(this.urlTag)) {
      transmissionTagTracker.returnTag(this.urlTag)
      this.urlTag = ''
    }
  }
}
